import type { NotificationType } from '@affine/graphql';

type DocNotificationType =
  | NotificationType.DocShared
  | NotificationType.DocRoleUpdated
  | NotificationType.DocRoleRevoked;

type FolderNotificationType =
  | NotificationType.FolderShared
  | NotificationType.FolderRoleRevoked;

type PermissionNotificationBody = {
  actorName?: string | null;
};

type DocNotificationBody = PermissionNotificationBody & {
  docName?: string | null;
  role?: string;
};

type FolderNotificationBody = PermissionNotificationBody & {
  folderName?: string;
  resourceKind?: 'folder' | 'team_area';
  role?: string;
};

type FolderNotificationTranslator = {
  Untitled(): string;
  'com.streamlain.notification.folder-shared'(params: {
    folderName: string;
    role: string;
  }): string;
  'com.streamlain.notification.folder-role-revoked'(params: {
    folderName: string;
  }): string;
  'com.streamlain.notification.team-area-shared'(params: {
    areaName: string;
    role: string;
  }): string;
  'com.streamlain.notification.team-area-role-revoked'(params: {
    areaName: string;
  }): string;
  'com.streamlain.notification.doc-shared'(params: { role: string }): string;
  'com.streamlain.notification.doc-role-updated'(params: {
    role: string;
  }): string;
  'com.streamlain.notification.doc-role-revoked'(): string;
};

function withActorAndResource(
  message: string,
  body: PermissionNotificationBody,
  resourceName: string
): string {
  const actorPrefix = body.actorName ? `${body.actorName}: ` : '';
  return `${actorPrefix}${message} — “${resourceName}”`;
}

export function formatDocNotification(
  t: FolderNotificationTranslator,
  type: DocNotificationType,
  body: DocNotificationBody
): string {
  const name = body.docName || t.Untitled();
  const role = body.role || 'Reader';
  const message =
    type === 'DocShared'
      ? t['com.streamlain.notification.doc-shared']({ role })
      : type === 'DocRoleUpdated'
        ? t['com.streamlain.notification.doc-role-updated']({ role })
        : t['com.streamlain.notification.doc-role-revoked']();
  return withActorAndResource(message, body, name);
}

export function formatFolderNotification(
  t: FolderNotificationTranslator,
  type: FolderNotificationType,
  body: FolderNotificationBody
): string {
  const name = body.folderName || t.Untitled();

  if (type === 'FolderShared') {
    const role = body.role || 'Reader';
    const message =
      body.resourceKind === 'team_area'
        ? t['com.streamlain.notification.team-area-shared']({
            areaName: name,
            role,
          })
        : t['com.streamlain.notification.folder-shared']({
            folderName: name,
            role,
          });
    return withActorAndResource(message, body, name);
  }

  const message =
    body.resourceKind === 'team_area'
      ? t['com.streamlain.notification.team-area-role-revoked']({
          areaName: name,
        })
      : t['com.streamlain.notification.folder-role-revoked']({
          folderName: name,
        });
  return withActorAndResource(message, body, name);
}
